// CRM Email Composer: 1-1 email to a contact from a connected mailbox, CRM style.
// Opened from the contact record (Email button, Emails tab, Reply on a timeline item)
// and from the company record's Activity tab. Sends through POST /contacts/:id/email,
// which writes the message to the contact's timeline. The From picker offers the
// operator's personal inbox first, then the client's sending mailboxes.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';
import ReplyEditor from '../../components/ReplyEditor';

const FROM_KEY = 'sonar.compose.from';

function readStoredFrom() { try { return parseInt(window.localStorage.getItem(FROM_KEY)) || null; } catch (_) { return null; } }
function storeFrom(id) { try { window.localStorage.setItem(FROM_KEY, String(id)); } catch (_) {} }
function bodyIsEmpty(html) { return !String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim(); }
function reSubject(s) { const v = String(s || '').trim(); return /^re:/i.test(v) ? v : ('Re: ' + v).trim(); }

export default function CrmEmailComposer({ clientId, contact, replyTo, onClose, onSent }) {
  const { getToken } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [loadingMb, setLoadingMb] = useState(true);
  const [fromId, setFromId] = useState(null);
  const [subject, setSubject] = useState(replyTo ? reSubject(replyTo.subject) : '');
  const [html, setHtml] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [withSig, setWithSig] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sigEdit, setSigEdit] = useState(null); // null | html being edited
  const [sigSaving, setSigSaving] = useState(false);
  const pollRef = useRef(null);

  const loadMailboxes = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/clients/${clientId}/send-mailboxes`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Could not load mailboxes (${r.status})`);
      const list = await r.json();
      setMailboxes(Array.isArray(list) ? list : []);
      return Array.isArray(list) ? list : [];
    } catch (e) { setError(e.message); return []; }
    finally { setLoadingMb(false); }
  }, [clientId, getToken]);

  useEffect(() => {
    loadMailboxes().then(list => {
      const usable = list.filter(m => m.status === 'active');
      const stored = readStoredFrom();
      const pick = usable.find(m => m.id === stored) || usable.find(m => m.is_personal) || usable[0];
      if (pick) setFromId(pick.id);
    });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadMailboxes]);

  const from = mailboxes.find(m => m.id === fromId) || null;
  const personal = mailboxes.filter(m => m.is_personal);
  const clientBoxes = mailboxes.filter(m => !m.is_personal);
  const brokenPersonal = personal.filter(m => m.status !== 'active');
  const hasActivePersonal = personal.some(m => m.status === 'active');
  const signatureHtml = withSig && from ? (from.signature_html || (from.signature_text ? from.signature_text.replace(/\n/g, '<br>') : '')) : '';

  const connectPersonal = (provider) => {
    const back = `/clients/${clientId}/crm/contacts/${contact.id}`;
    const url = `${API_BASE}/oauth/${provider}/connect?personal=1&client_id=${clientId}&return_to=${encodeURIComponent(back)}`;
    window.open(url, '_blank', 'noopener');
    toast('Finish signing in on the new tab. Your inbox appears here once it is connected.', { icon: '↗' });
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      const list = await loadMailboxes();
      const fresh = list.find(m => m.is_personal && m.status === 'active');
      if (fresh || ticks >= 40) {
        clearInterval(pollRef.current); pollRef.current = null;
        if (fresh) { setFromId(fresh.id); toast.success(`${fresh.email_address} connected`); }
      }
    }, 3000);
  };

  // Signature lives on the mailbox, so it is set once and used on every email from it.
  const openSigEdit = () => {
    if (!from) return;
    setSigEdit(from.signature_html || (from.signature_text ? from.signature_text.replace(/\n/g, '<br>') : ''));
  };
  const saveSig = async () => {
    if (!from) return;
    setSigSaving(true);
    try {
      const token = await getToken();
      const text = String(sigEdit || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div)>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\n{3,}/g, '\n\n').trim();
      const res = await fetch(`${API_BASE}/mailboxes/${from.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_html: text ? sigEdit : null, signature_text: text || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not save signature (${res.status})`);
      await loadMailboxes();
      setSigEdit(null);
      setWithSig(true);
      toast.success('Signature saved');
    } catch (e) { toast.error(e.message); }
    finally { setSigSaving(false); }
  };

  const close = () => {
    if (!bodyIsEmpty(html) && !window.confirm('Discard this email?')) return;
    onClose();
  };

  const send = async () => {
    setError(null);
    if (!from) { setError('Choose a mailbox to send from'); return; }
    if (!subject.trim()) { setError('Add a subject'); return; }
    if (bodyIsEmpty(html)) { setError('The email is empty'); return; }
    setSending(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/contacts/${contact.id}/email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mailbox_id: from.id, subject: subject.trim(), html,
          cc: showCc ? cc : '', bcc: showCc ? bcc : '',
          include_signature: withSig,
          reply_to_type: replyTo ? replyTo.type : undefined,
          reply_to_id: replyTo ? replyTo.id : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Send failed (${res.status})`);
      storeFrom(from.id);
      toast.success(`Sent to ${contact.full_name || contact.email}`);
      onSent && onSent(data.message);
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); if (!sending) send(); }
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  return (
    <div className="crmr-modal-overlay" onMouseDown={close}>
      <div className="crmec" onMouseDown={e => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div className="crmec-head">
          <h2>{replyTo ? 'Reply' : 'New email'}</h2>
          <button className="crmec-x" onClick={close} aria-label="Close">×</button>
        </div>

        {!loadingMb && !hasActivePersonal && (
          <div className="crmec-connect">
            <div className="crmec-connect-t">{brokenPersonal.length ? `${brokenPersonal[0].email_address} needs reconnecting` : 'Send from your own inbox'}</div>
            <div className="crmec-connect-s">Emails go out from your normal address and replies sync back to this record. Only mail with existing contacts is ever stored.</div>
            <div className="crmec-connect-b">
              <button className="crmr-act crmr-act-ghost" onClick={() => connectPersonal('google')}>Connect Gmail or Google Workspace</button>
              <button className="crmr-act crmr-act-ghost" onClick={() => connectPersonal('microsoft')}>Connect Outlook</button>
            </div>
          </div>
        )}

        <div className="crmec-row">
          <span className="crmec-lbl">From</span>
          {loadingMb ? <span className="crmec-muted">Loading mailboxes…</span>
            : mailboxes.filter(m => m.status === 'active').length === 0 ? <span className="crmec-muted">No connected mailbox yet</span>
            : (
              <select className="crmec-select" value={fromId || ''} onChange={e => setFromId(parseInt(e.target.value))}>
                {personal.length > 0 && (
                  <optgroup label="Your inbox">
                    {personal.map(m => <option key={m.id} value={m.id} disabled={m.status !== 'active'}>{m.display_name ? `${m.display_name} · ${m.email_address}` : m.email_address}{m.status !== 'active' ? ' (reconnect)' : ''}</option>)}
                  </optgroup>
                )}
                {clientBoxes.length > 0 && (
                  <optgroup label="Client sending mailboxes">
                    {clientBoxes.map(m => <option key={m.id} value={m.id}>{m.display_name ? `${m.display_name} · ${m.email_address}` : m.email_address}</option>)}
                  </optgroup>
                )}
              </select>
            )}
        </div>

        <div className="crmec-row">
          <span className="crmec-lbl">To</span>
          <span className="crmec-to"><b>{contact.full_name || contact.email}</b>{contact.full_name ? <span className="crmec-muted"> &lt;{contact.email}&gt;</span> : null}</span>
          {!showCc && <button className="crmec-link" onClick={() => setShowCc(true)}>Cc / Bcc</button>}
        </div>
        {showCc && (
          <>
            <div className="crmec-row"><span className="crmec-lbl">Cc</span><input className="crmec-input" value={cc} onChange={e => setCc(e.target.value)} placeholder="name@company.com, another@company.com" /></div>
            <div className="crmec-row"><span className="crmec-lbl">Bcc</span><input className="crmec-input" value={bcc} onChange={e => setBcc(e.target.value)} placeholder="Copy yourself or your CRM here" /></div>
          </>
        )}
        <div className="crmec-row">
          <span className="crmec-lbl">Subject</span>
          <input className="crmec-input crmec-input--subj" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" autoFocus={!replyTo} />
        </div>

        <div className="crmec-body">
          <ReplyEditor value={html} onChange={setHtml} clientId={clientId} placeholder={`Write to ${(contact.full_name || '').split(' ')[0] || 'them'}…`} signatureHtml={signatureHtml} />
        </div>

        {sigEdit !== null && (
          <div className="crmec-sigedit">
            <div className="crmec-sigedit-h">Signature for {from ? from.email_address : 'this mailbox'}<span className="crmec-muted"> · used on every email sent from it</span></div>
            <ReplyEditor value={sigEdit} onChange={setSigEdit} placeholder="Name, role, company, phone" />
            <div className="crmec-sigedit-b">
              <button className="crmr-act crmr-act-ghost" onClick={() => setSigEdit(null)} disabled={sigSaving}>Cancel</button>
              <button className="crmr-act crmr-act-primary" onClick={saveSig} disabled={sigSaving}>{sigSaving ? 'Saving…' : 'Save signature'}</button>
            </div>
          </div>
        )}

        {error && <div className="crmec-err">{error}</div>}

        <div className="crmec-foot">
          <label className="crmec-sig">
            <input type="checkbox" checked={withSig} onChange={e => setWithSig(e.target.checked)} />
            Add signature
          </label>
          {from && sigEdit === null && (
            <button className="crmec-link" onClick={openSigEdit}>{(from.signature_html || from.signature_text) ? 'Edit signature' : 'Create signature'}</button>
          )}
          <span className="crmec-spacer" />
          <span className="crmec-hint">⌘ Enter to send</span>
          <button className="crmr-act crmr-act-ghost" onClick={close} disabled={sending}>Discard</button>
          <button className="crmr-act crmr-act-primary" onClick={send} disabled={sending || !from}>{sending ? 'Sending…' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}
